import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, googleProvider } from "../firebase";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import Loader from "../utils/Loader";
import { toast } from "react-toastify";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const handleCheckAccess = async () => {
    try {
      const url = `${
        process.env.REACT_APP_FIREBASE_BASE_API
      }/getUserAccess?email=${encodeURIComponent(email)}`;

      const response = await fetch(url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const data = await response.json();

      // Check if any invoiceSystem permission is true
      const hasInvoiceSystemAccess = checkInvoiceSystemAccess(
        data.permissions.invoiceSystem
      );

      setError("");
      return hasInvoiceSystemAccess;
    } catch (err) {
      setError(err.message);
    }
  };

  const checkInvoiceSystemAccess = (data) => {
    return !!(
      data.receipts.invoicesSubAccess ||
      data.receipts.noticeChargesAccess ||
      data.receipts.ackReceiptsAccess ||
      data.records.internal.caseBriefAccess ||
      data.records.internal.executiveAdminAccess ||
      data.records.internal.secretarialLogsAccess ||
      data.records.internal.processServerLogsAccess ||
      data.records.external.transmittalAccess ||
      data.records.external.legalAdviceAccess ||
      data.records.external.secretariatResolutionsAccess
    );
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setLoadingText("Checking access, please wait...");
      await signInWithEmailAndPassword(auth, email, password);

      // Use returned value from handleCheckAccess
      const accessGranted = await handleCheckAccess();
      if (accessGranted) {
        navigate("/"); // Proceed if access is granted
      } else {
        setError("Access denied. Please contact UP-Hub.");
        toast.error("Access denied. Please contact UP-Hub.");
        await signOut(auth); // Log out if no access
      }
    } catch (err) {
      setError("Invalid email or password. Please try again.");
      toast.error("Invalid email or password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setLoadingText("Checking access, please wait...");
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      setEmail(user.email);

      // Use returned value from handleCheckAccess
      const accessGranted = await handleCheckAccess();
      if (accessGranted) {
        navigate("/");
      } else {
        setError("Access denied. Please contact UP-Hub.");
        toast.error("Access denied. Please contact UP-Hub.");
        await signOut(auth);
      }
    } catch (err) {
      console.error("Google Login Error:", err.message);
      if (err.code === "auth/popup-closed-by-user") {
        setError("Google login was canceled. Please try again.");
        toast.error("Google login was canceled. Please try again.");
      } else if (err.code === "auth/network-request-failed") {
        setError("Network error. Please check your internet connection.");
        toast.error("Network error. Please check your internet connection.");
      } else {
        setError("Google login failed. Please try again.");
        toast.error("Google login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-custom h-[calc(100vh-80px)] flex justify-center items-center mt- px-4">
      {loading && <Loader text={loadingText} />}
      <div className="shadow-2xl rounded-2xl border flex flex-col sm:flex-row overflow-hidden max-w-lg sm:max-w-5xl w-full">
        {/* Left Section */}
        <div className="flex-1 w-full p-6 sm:p-12 bg-transparent text-color">
          <h2 className="text-3xl font-boldmb-6">Welcome back!</h2>
          <div className="w-2/3 border-t-4 rounded-lg border-tier-secondary my-4"></div>
          <form onSubmit={handleEmailLogin} className="space-y-6">
            <div>
              <label className="block text-alt-color font-medium">Email</label>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="w-full border border-alt-color rounded-lg text-tier-primary px-4 py-3 mt-1 outline-none focus:ring-teal-500 focus:border-teal-500"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <span className="absolute right-4 top-3 text-tier-secondary material-symbols-outlined">
                  mail
                </span>
              </div>
            </div>
            <div>
              <label className="block text-alt-color font-medium">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  className="w-full border border-alt-color rounded-lg text-tier-primary px-4 py-3 mt-1 outline-none focus:ring-teal-500 focus:border-teal-500"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-3 text-tier-secondary focus:outline-none"
                >
                  <span className="material-symbols-outlined">
                    {showPassword ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-tier-secondary text-color py-3 rounded-lg shadow-lg hover:bg-dark-alt hover:text-tier-primary transition duration-300"
            >
              Login
            </button>
          </form>

          {/* Google Login Button */}
          <div className="mt-6">
            <button
              onClick={handleGoogleLogin}
              className="w-full flex items-center justify-center border border-alt-color text-tier-primary bg-white py-3 rounded-lg shadow-md hover:shadow-lg hover:bg-gray-100 transition duration-300"
            >
              <img
                src="/icons/google.png"
                alt="Google Logo"
                className="w-8 h-8 mr-3"
              />
              Continue with Google
            </button>
          </div>

          {error && (
            <p className="text-red-500 text-center text-sm mt-4">{error}</p>
          )}
        </div>

        {/* Right Section */}
        <div className="hidden sm:flex flex-1 items-center justify-center bg-white/20 text-color p-12">
          <div className="text-center">
            <div className="w-full flex flex-col items-center justify-center text-center my-4">
              <picture>
                <source type="image/webp" srcSet={"/icons/logo.webp"} />
                <source type="image/png" srcSet={"/icons/logo.png"} />
                <img
                  src={"/icons/logo.png"}
                  alt="Tools by UP-Hub"
                  className="w-52 h-auto mx-auto"
                />
              </picture>
              <h3 className="mt-2 text-2xl sm:text-4xl text-color font-luthier font-semibold">
                UNDERGROUND
                <br />
                PROJECT
              </h3>
            </div>
            <p className="text-alt-color leading-relaxed">
              Access a variety of tools to streamline your workflow, including
              invoicing, document editing, and project management—all powered by
              UP-Hub.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
